/**
 * 관리자 페이지 타입 설정
 *
 * QRO 관리자 페이지: 오프라인 매장/스마트오더 중심 (기존 /admin)
 * MYSHOP 관리자 페이지: 온라인 쇼핑몰 관리 중심 (신규 /myshop)
 */

// 관리자 타입 상수
export const ADMIN_TYPE = {
  QRO: 'qro',           // QRO 관리자 페이지 (오프라인/스마트오더)
  MYSHOP: 'myshop'      // MYSHOP 관리자 페이지 (온라인 쇼핑몰)
}

// 관리자 타입별 브랜딩 설정 (Modern Color Scheme)
export const ADMIN_BRANDING = {
  [ADMIN_TYPE.QRO]: {
    title: 'QRO 관리자',
    fullTitle: 'QRO 관리자 페이지',
    description: '오프라인 매장 및 스마트오더 관리',
    primaryColor: '#4F46E5',     // Indigo 600 - 세련된 인디고
    logo: '/images/qro-logo.png',
    basePath: '/admin'
  },
  [ADMIN_TYPE.MYSHOP]: {
    title: 'MYSHOP 관리자',
    fullTitle: 'MYSHOP 관리자 페이지',
    description: '온라인 쇼핑몰 관리',
    primaryColor: '#7C3AED',     // Violet 600 - 모던 바이올렛
    logo: '/images/myshop-logo.png',
    basePath: '/myshop'
  }
}

// 라우트 경로로 관리자 타입 판별
export function getAdminTypeFromPath(path) {
  if (path.startsWith('/myshop')) {
    return ADMIN_TYPE.MYSHOP
  }
  return ADMIN_TYPE.QRO
}

// 관리자 타입별 브랜딩 정보 가져오기
export function getAdminBranding(adminType) {
  return ADMIN_BRANDING[adminType] || ADMIN_BRANDING[ADMIN_TYPE.QRO]
}

// MYSHOP 관리자 메뉴 구조 (shockping-admin 기반)
export const MYSHOP_MENU = {
  groups: [
    {
      title: '대시보드',
      items: [
        { title: '홈', path: '/myshop', icon: 'fa-home' }
      ]
    },
    {
      title: '상품 관리',
      items: [
        { title: '상품 목록', path: '/myshop/product/list', icon: 'fa-box' },
        { title: '상품 등록', path: '/myshop/product/create', icon: 'fa-plus' },
        { title: '카테고리 관리', path: '/myshop/product/category-list', icon: 'fa-folder' },
        { title: '옵션 관리', path: '/myshop/product/option-list', icon: 'fa-list' },
        { title: '이미지 갤러리', path: '/myshop/product/image-list', icon: 'fa-images' }
      ]
    },
    {
      title: '주문 관리',
      items: [
        { title: '주문 목록', path: '/myshop/order/list', icon: 'fa-shopping-cart' },
        { title: '주문 확정', path: '/myshop/order/finalized', icon: 'fa-check-circle' },
        { title: '상품별 주문현황', path: '/myshop/order/product_summary', icon: 'fa-chart-bar' },
        { title: '세금계산서', path: '/myshop/order/vat', icon: 'fa-file-invoice' }
      ]
    },
    {
      title: '회원 관리',
      items: [
        { title: '가입 신청 현황', path: '/myshop/member/pending', icon: 'fa-user-clock' },
        { title: '회원 목록', path: '/myshop/member/list', icon: 'fa-users' },
        { title: '회원 포인트', path: '/myshop/member/point', icon: 'fa-coins' }
      ]
    },
    {
      title: '쿠폰/포인트',
      items: [
        { title: '쿠폰 목록', path: '/myshop/coupon/list', icon: 'fa-ticket-alt' },
        { title: '쿠폰 생성', path: '/myshop/coupon/create', icon: 'fa-plus-circle' },
        { title: '포인트 관리', path: '/myshop/point/list', icon: 'fa-star' }
      ]
    },
    // 아래 두 묶음은 백엔드가 있어야 보인다(feature) — 실제 사이드 메뉴는 App.vue(menu_type 10)가 loadMyshopFeatures 결과로 그린다
    {
      title: '파트너 판매',
      feature: 'partner',   // Java GET /api/shopadm/partner/program 이 available:true 일 때
      items: [
        { title: '파트너 판매 설정', path: '/myshop/partner/setting', icon: 'fa-handshake' },
        { title: '파트너', path: '/myshop/partner/list', icon: 'fa-users' },
        { title: '상품별 요율', path: '/myshop/partner/products', icon: 'fa-tags' },
        { title: '정산 장부', path: '/myshop/partner/ledger', icon: 'fa-book' }
      ]
    },
    {
      title: 'SNS 판매 자동화',
      feature: 'social',    // core-api GET /core-api/social/instagram/status 가 enabled·allowed 일 때
      items: [
        { title: '개요', path: '/myshop/social', icon: 'fa-chart-line' },
        { title: '자동화', path: '/myshop/social/rules', icon: 'fa-robot' },
        { title: '인스타 게시물', path: '/myshop/social/posts', icon: 'fa-images' },
        { title: '인스타 공구', path: '/myshop/social/groupbuy', icon: 'fa-users' },
        { title: '콘텐츠 성과', path: '/myshop/social/content', icon: 'fa-photo-video' },
        { title: '발송 기록', path: '/myshop/social/deliveries', icon: 'fa-paper-plane' },
        { title: '영상 게시', path: '/myshop/social/publish', icon: 'fa-film' },
        { title: '인스타그램 연결', path: '/myshop/instagram', icon: 'fa-link' },
        { title: '유튜브 연결', path: '/myshop/youtube', icon: 'fa-link' }
      ]
    },
    {
      title: '디자인',
      items: [
        { title: '스킨 설정', path: '/myshop/design/skin', icon: 'fa-palette' },
        { title: '디자인 목록', path: '/myshop/design/list', icon: 'fa-paint-brush' }
      ]
    },
    {
      title: '콘텐츠 관리',
      items: [
        { title: '페이지 관리', path: '/myshop/page/list', icon: 'fa-file-alt' },
        { title: '이미지 관리', path: '/myshop/image/list', icon: 'fa-image' },
        { title: '공지사항', path: '/myshop/notice/list', icon: 'fa-bullhorn' }
      ]
    },
    {
      title: 'Q&A/리뷰',
      items: [
        { title: 'Q&A 관리', path: '/myshop/qna/list', icon: 'fa-question-circle' },
        { title: '리뷰 관리', path: '/myshop/review/list', icon: 'fa-comment' }
      ]
    },
    {
      title: '설정',
      items: [
        { title: '샵 설정', path: '/myshop/setting', icon: 'fa-cog' },
        { title: '배송 설정', path: '/myshop/product/delivery', icon: 'fa-truck' }
      ]
    }
  ]
}

export default {
  ADMIN_TYPE,
  ADMIN_BRANDING,
  MYSHOP_MENU,
  getAdminTypeFromPath,
  getAdminBranding
}
